import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { payrollOpsService, payrollService } from "../../../services/api";

const DEFAULT_WORKFLOW = {
  finalizeMode: "direct",
  approvalMethod: "workflow",
  workflowSteps: [],
  workflowParallelApproval: false,
  workflowEscalationAfterDays: 3,
  workflowAutoApprovalIfNoAction: false,
};

export default function PayrollStepsSettingsTab({
  darkMode = false,
  payrollSettings = null,
  canEdit = true,
  onSave,
}) {
  const [catalog, setCatalog] = useState([]);
  const [stepsConfig, setStepsConfig] = useState([]);
  const [workflow, setWorkflow] = useState(DEFAULT_WORKFLOW);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await payrollOpsService.getStepCatalog();
        if (cancelled) return;
        setCatalog(res?.data?.catalog || []);
        const defaults = res?.data?.defaultSteps || [];
        const saved = payrollSettings?.payrollStepsJson;
        if (Array.isArray(saved) && saved.length) {
          const legacyMap = { company_checklist: "checklist" };
          const byKey = new Map();
          saved.forEach((s) => {
            const key = legacyMap[s.key] || s.key;
            byKey.set(key, { ...s, key, label: key === "checklist" ? "Checklist" : s.label });
          });
          const merged = defaults.map((d) => {
            const prev = byKey.get(d.key);
            return prev ? { ...d, ...prev, key: d.key, label: d.label } : d;
          });
          setStepsConfig(merged);
        } else {
          setStepsConfig(defaults);
        }
        setWorkflow({
          ...DEFAULT_WORKFLOW,
          ...(payrollSettings?.payrollWorkflowJson || res?.data?.defaultWorkflow || {}),
        });
      } catch {
        if (!cancelled) toast.error("Failed to load payroll step catalog");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payrollSettings]);

  const toggleStep = (key, field) => {
    setStepsConfig((prev) =>
      prev.map((s) => (s.key === key ? { ...s, [field]: !s[field] } : s))
    );
  };

  const toggleField = (stepKey, fieldKey, prop) => {
    setStepsConfig((prev) =>
      prev.map((s) => {
        if (s.key !== stepKey) return s;
        const fields = (s.fields || []).map((f) =>
          f.key === fieldKey ? { ...f, [prop]: !f[prop] } : f
        );
        return { ...s, fields };
      })
    );
  };

  const handleSave = async () => {
    if (!canEdit) return;
    try {
      const payload = {
        payrollStepsJson: stepsConfig,
        payrollWorkflowJson: workflow,
        isInvestmentEnabled: !!payrollSettings?.isInvestmentEnabled,
        isProofEnabled: !!payrollSettings?.isProofEnabled,
        allowMidMonthIncrements: !!payrollSettings?.allowMidMonthIncrements,
      };
      const res = await payrollService.upsertPayrollSettings(payload);
      if (res.success) {
        toast.success("Payroll setup saved");
        onSave?.(res.data);
      }
    } catch (err) {
      toast.error(err?.message || "Save failed");
    }
  };

  if (loading) return <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading…</p>;

  const card = darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]";

  return (
    <div className="space-y-5">
      <div className={`rounded-xl border p-4 ${card}`}>
        <h3 className="mb-3 text-sm font-semibold">Validation steps</h3>
        <p className={`mb-3 text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
          Configure which steps are mandatory and which columns appear in Run Payroll validation grids.
        </p>
        <div className="space-y-3">
          {stepsConfig.map((step) => {
            const cat = catalog.find((c) => c.key === step.key);
            return (
              <div key={step.key} className={`rounded border p-3 text-xs ${darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"}`}>
                <div className="mb-2 flex flex-wrap items-center gap-3">
                  <span className="font-semibold">{cat?.label || step.label}</span>
                  <label className="flex items-center gap-1">
                    <input type="checkbox" checked={step.enabled !== false} onChange={() => toggleStep(step.key, "enabled")} />
                    Enabled
                  </label>
                  <label className="flex items-center gap-1">
                    <input type="checkbox" checked={!!step.blocking} onChange={() => toggleStep(step.key, "blocking")} />
                    Mandatory
                  </label>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                  {(step.fields || []).map((f) => (
                    <label
                      key={f.key}
                      className="inline-flex items-center gap-1"
                    >
                      <input
                        type="checkbox"
                        checked={f.visible !== false}
                        onChange={() => toggleField(step.key, f.key, "visible")}
                      />
                      {f.label}
                      {f.required !== false && (
                        <input
                          type="checkbox"
                          title="Required"
                          checked={f.required !== false}
                          onChange={() => toggleField(step.key, f.key, "required")}
                        />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`rounded-xl border p-4 ${card}`}>
        <h3 className="mb-3 text-sm font-semibold">Finalise unlock workflow</h3>
        <p className={`mb-3 text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
          Process Payroll stays direct. This workflow only controls unlocking a finalised month.
        </p>
        <div className="space-y-3 text-xs">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={workflow.finalizeMode === "direct"}
              onChange={() => setWorkflow((w) => ({ ...w, finalizeMode: "direct" }))}
            />
            Direct unlock (no approval)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={workflow.finalizeMode === "approval_workflow"}
              onChange={() => setWorkflow((w) => ({ ...w, finalizeMode: "approval_workflow" }))}
            />
            Approval workflow before unlock
          </label>
          {workflow.finalizeMode === "approval_workflow" && (
            <div className="ml-4 space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!workflow.workflowParallelApproval}
                  onChange={(e) =>
                    setWorkflow((w) => ({ ...w, workflowParallelApproval: e.target.checked }))
                  }
                />
                Parallel approval
              </label>
              <label className="flex items-center gap-2">
                Escalation after (days)
                <input
                  type="number"
                  min="1"
                  className={`w-16 rounded border px-2 py-1 ${darkMode ? "border-[hsl(var(--ds-border))] bg-[#333]" : "border-[hsl(var(--ds-border))]"}`}
                  value={workflow.workflowEscalationAfterDays || 3}
                  onChange={(e) =>
                    setWorkflow((w) => ({
                      ...w,
                      workflowEscalationAfterDays: Number(e.target.value) || 3,
                    }))
                  }
                />
              </label>
            </div>
          )}
        </div>
      </div>

      {canEdit && (
        <button
          type="button"
          onClick={handleSave}
          className="rounded bg-[hsl(var(--ds-primary))] px-4 py-2 text-xs font-medium text-white"
        >
          Save payroll setup
        </button>
      )}
    </div>
  );
}
